import { getSupabaseClient } from "../providers/supabase/supabase";

import type {
  ConfigDraft,
  ConfigRelease,
  ControlPlanePayload,
  ControlPlaneState,
  CreateConfigDraftInput,
  RebaseSoundDraftResult,
  RuntimeConfigErrorCode,
} from "./contracts";
import { RuntimeConfigRequestError } from "./contracts";

export type ContactPaywallState = {
  environment: "stg" | "staging";
  default_enabled: boolean;
  override: boolean | null;
  enabled: boolean;
  release_id: string;
  digest: string;
  applied: boolean;
  can_edit: boolean;
};

export function fetchContactPaywall(whatsappId: string) {
  return post<ContactPaywallState>("/config/v1/contact-paywall/state", {
    whatsapp_id: whatsappId,
  });
}

export function setContactPaywall(
  whatsappId: string,
  override: boolean | null,
  expectedReleaseId: string,
) {
  return post<ContactPaywallState>("/config/v1/contact-paywall", {
    whatsapp_id: whatsappId,
    override,
    expected_release_id: expectedReleaseId,
  });
}

async function authHeaders(): Promise<Record<string, string>> {
  const { data } = await getSupabaseClient().auth.getSession();
  const token = data.session?.access_token;
  return token
    ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" };
}

function baseUrl(): string {
  return String(
    import.meta.env.VITE_CONFIG_CONTROL_PLANE_API_URL ?? "",
  ).replace(/\/$/, "");
}

async function request(path: string, init?: RequestInit): Promise<Response> {
  const base = baseUrl();
  if (!base) {
    throw new RuntimeConfigRequestError(
      0,
      "unconfigured",
      "control_plane_unconfigured",
    );
  }
  return fetch(`${base}${path}`, {
    ...init,
    headers: { ...(await authHeaders()), ...(init?.headers ?? {}) },
  });
}

export async function fetchControlPlaneState(): Promise<ControlPlaneState> {
  if (!baseUrl()) return { status: "unconfigured" };
  let response: Response;
  try {
    response = await request("/config/v1/state");
  } catch (error) {
    return {
      status: "error",
      message: error instanceof Error ? error.message : "network error",
    };
  }
  if (response.status === 401 || response.status === 403) {
    return { status: "unauthorized" };
  }
  if (!response.ok) {
    return {
      status: "error",
      message: `config state responded ${response.status}`,
    };
  }
  return {
    status: "ok",
    payload: (await response.json()) as ControlPlanePayload,
  };
}

export async function createConfigDraft(input: CreateConfigDraftInput) {
  return post<ConfigDraft>("/config/v1/drafts", input);
}

export async function validateConfigDraft(draft: ConfigDraft) {
  return post<ConfigDraft>(`/config/v1/drafts/${draft.id}/validate`, {
    expected_version: draft.version,
  });
}

export async function approveConfigDraft(draft: ConfigDraft) {
  return post<ConfigDraft>(`/config/v1/drafts/${draft.id}/approve`, {
    expected_version: draft.version,
  });
}

export async function publishConfigDraft(draft: ConfigDraft) {
  return post<ConfigRelease>(`/config/v1/drafts/${draft.id}/publish`, {});
}

export async function rebaseSoundConfigDraft(draft: ConfigDraft) {
  return post<RebaseSoundDraftResult>(
    `/config/v1/drafts/${draft.id}/rebase-sound`,
    { expected_version: draft.version },
  );
}

export async function approveAndPublishConfigDraft(draft: ConfigDraft) {
  const approved = await approveConfigDraft(draft);
  return publishConfigDraft(approved);
}

export async function rollbackConfigRelease(releaseId: string, reason: string) {
  return post<ConfigRelease>(
    `/config/v1/releases/${encodeURIComponent(releaseId)}/rollback`,
    { reason },
  );
}

async function post<T>(
  path: string,
  body: Record<string, unknown>,
): Promise<T> {
  const response = await request(path, {
    method: "POST",
    body: JSON.stringify(body),
  });
  const payload = (await response.json().catch(() => ({}))) as Record<
    string,
    unknown
  >;
  if (!response.ok) {
    throw toRequestError(response, payload);
  }
  return payload as T;
}

function toRequestError(response: Response, payload: Record<string, unknown>) {
  const upstreamCode = String(payload.error ?? `http_${response.status}`);
  const code: RuntimeConfigErrorCode =
    response.status === 401
      ? "unauthorized"
      : response.status === 403
        ? "forbidden"
        : response.status === 409
          ? "conflict"
          : response.status === 422
            ? "invalid"
            : response.status === 429
              ? "rate_limited"
              : response.status >= 500
                ? "dependency_unavailable"
                : "unknown";
  const retryAfter = Number(response.headers.get("Retry-After"));
  return new RuntimeConfigRequestError(
    response.status,
    code,
    upstreamCode,
    Number.isFinite(retryAfter) ? retryAfter : null,
    payload.details &&
    typeof payload.details === "object" &&
    !Array.isArray(payload.details)
      ? (payload.details as Record<string, unknown>)
      : {},
  );
}
